import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Edit,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  RotateCcw,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";
import {
  getStatusBadgeColor,
  getStatusDisplayName,
  formatPaymentMethod,
  type PaymentStatus,
} from "../utils/payments";

interface Payment {
  id: string;
  created_at: string;
  student_id: string;
  run_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  notes: string | null;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  currency: string | null;
  status: string;
}

interface PaymentWithDetails extends Payment {
  student?: {
    id: string;
    codername: string;
    parentname: string;
    email: string;
    age: number;
  };
  run?: {
    id: string;
    name: string;
    day_of_week: string[];
    start_time: string;
    price: number;
    series: {
      id: string;
      title: string;
      code: string;
    };
  };
}

const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [payment, setPayment] = useState<PaymentWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { userRole } = useAuth();

  const isAdmin = userRole === "admin";

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        setIsLoading(true);

        if (!id) return;

        const { data, error } = await supabase
          .from("payments")
          .select(
            `
            *,
            student:student_id (
              id,
              codername,
              parentname,
              email,
              age
            ),
            run:run_id (
              id,
              name,
              day_of_week,
              start_time,
              price,
              series:series_id (
                id,
                title,
                code
              )
            )
          `
          )
          .eq("id", id)
          .single();

        if (error) {
          console.error("Supabase error details:", error);
          throw error;
        }

        console.log("Payment data from DB:", data);

        if (data) {
          setPayment(data);
        } else {
          setPayment(null);
        }
      } catch (error) {
        console.error("Error fetching payment:", error);
        toast.error("Failed to load payment details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayment();
  }, [id]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case "partial":
        return <Clock className="h-6 w-6 text-blue-500" />;
      case "failed":
        return <XCircle className="h-6 w-6 text-red-500" />;
      case "cancelled":
        return <XCircle className="h-6 w-6 text-orange-500" />;
      case "refunded":
        return <RotateCcw className="h-6 w-6 text-gray-500" />;
      case "not_received":
        return <XCircle className="h-6 w-6 text-red-500" />;
      case "pending":
      default:
        return <Clock className="h-6 w-6 text-yellow-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center mb-6">
          <div className="w-24 h-8 bg-gray-200 rounded"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="w-48 h-8 bg-gray-200 rounded mb-4"></div>
          <div className="w-full h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-12">
        <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Payment not found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          The payment you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <SmartBackButton fallbackPath="/payments" />
        </div>
      </div>
    );
  }

  const paymentStatus = payment
    ? (payment.status as PaymentStatus)
    : "not_received";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SmartBackButton fallbackPath="/payments" />
        </div>
        {isAdmin && (
          <div className="flex items-center space-x-3">
            <Link to={`/payments/${payment.id}/edit`} className="btn btn-edit">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Link>
            {/* <button onClick={handleDeletePayment} className="btn btn-danger">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button> */}
          </div>
        )}
      </div>

      {/* Payment Overview Card */}
      <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <h1 className="h1">{ENTITY_NAMES.PAYMENT} Details</h1>
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
                paymentStatus
              )}`}
            >
              <StatusIcon status={paymentStatus} />
              <span className="ml-2">
                {getStatusDisplayName(paymentStatus)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Information */}
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Amount</label>
                  <p className="text-2xl">{formatCurrency(payment.amount)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium">For</label>
                  <div className="">
                    <div className="font-medium">
                      {payment.student ? (
                        <Link
                          to={`/students/${payment.student.id}`}
                          className="font-medium"
                        >
                          {payment.student.codername}
                        </Link>
                      ) : (
                        "Unknown Student"
                      )}
                    </div>
                    <div className="">
                      {payment.run ? (
                        <>
                          <Link to={`/runs/${payment.run.id}`} className="">
                            Group {payment.run.name}
                          </Link>
                          {payment.run.series && (
                            <>
                              {" "}
                              of{" "}
                              <Link
                                to={`/series/${payment.run.series.id}`}
                                className=""
                              >
                                {payment.run.series.code}
                              </Link>
                            </>
                          )}
                        </>
                      ) : (
                        "No Group"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* For Information */}
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Payment Date</label>
                  <p className="">{formatDate(payment.payment_date)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium">Payment Method</label>
                  <p className="">
                    {formatPaymentMethod(
                      payment.payment_method || "",
                      paymentStatus
                    )}
                  </p>
                </div>

                {payment.notes && (
                  <div>
                    <label className="text-sm font-medium">Notes</label>
                    <p className="">{payment.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Card */}
      <div className="bg-white shadow-sm rounded-lg border border-neutral-200 p-6">
        <h2 className="mb-4">Payment IDs & Metadata</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {payment.stripe_payment_intent_id && (
            <div>
              <div className="text-sm font-medium">Stripe Payment Intent</div>
              <div className="">{payment.stripe_payment_intent_id}</div>
            </div>
          )}
          {payment.stripe_session_id && (
            <div>
              <div className="text-sm font-medium">Stripe Session</div>
              <div className="">{payment.stripe_session_id}</div>
            </div>
          )}
          {payment.stripe_customer_id && (
            <div>
              <div className="text-sm font-medium">Stripe Customer:</div>
              <div className="">{payment.stripe_customer_id}</div>
            </div>
          )}
          <div>
            <div className="text-sm font-medium">Database ID:</div>
            <div className="">{payment.id}</div>
          </div>
          <div>
            <div className="text-sm font-medium">Created:</div>
            <div className="">
              {new Date(payment.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
